import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Department, useDepartmentDestroyMutation } from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { ListQueryResultWithIds } from 'utils/fetch/types';
import { APP_URLS } from 'constants/urls';

export default function useDepartmentsListActions(props: {
  departmentsQueryHookResult: ListQueryResultWithIds<Department>;
  onDestroyed?: (id: string) => void;
  useBulkSelectResult: UseBulkSelectResult<Department>;
  useModalManagementResult: UseModalManagementResult;
}) {
  const {
    departmentsQueryHookResult,
    onDestroyed,
    useBulkSelectResult,
    useModalManagementResult,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { selectedSingleTableItem, handleSelectSingleTableItem } =
    useBulkSelectResult;
  const { closeModal, openModal } = useModalManagementResult;

  const selectedDepartmentId = selectedSingleTableItem?.id || null;

  const [destroyMutation, destroyMutationResult] = useDepartmentDestroyMutation(
    {
      onCompleted: () => {
        closeModal(Modals.DepartmentDestroyModal);
        toast({ content: t('destroy-department-success') });
        if (selectedDepartmentId) onDestroyed?.(selectedDepartmentId);
        onDestroyListItemCallBack({
          collection: departmentsQueryHookResult.collection,
          navigate,
          pathname,
          refetch: departmentsQueryHookResult.refetch,
          search,
          totalPages: departmentsQueryHookResult.totalPages,
        });
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    },
  );

  const onCreate = () => {
    navigate(APP_URLS.dashboard.departments.add.path);
  };

  const onView = (item?: Department) => {
    if (item?.id) {
      navigate(
        APP_URLS.dashboard.departments.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    }
  };

  const onEdit = (item?: Department) => {
    if (item?.id) {
      navigate(
        APP_URLS.dashboard.departments.edit.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    }
  };

  const onDelete = (item?: Department) => {
    if (item?.id) {
      handleSelectSingleTableItem(item);
      openModal(Modals.DepartmentDestroyModal);
    }
  };

  const onConfirmedDelete = () => {
    if (selectedDepartmentId) {
      destroyMutation({
        variables: {
          id: selectedDepartmentId,
        },
      });
    }
  };

  return {
    destroyLoading: destroyMutationResult.loading,
    onConfirmedDelete,
    onCreate,
    onDelete,
    onEdit,
    onView,
    selectedDepartmentId,
  };
}
